import { useState } from 'react';
import { Upload, Button, message, Empty, Spin } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type MediaRow } from '../api/client';
import PageHeader from '../components/PageHeader';
import MediaFilterBar, { type MediaFilters } from './media/MediaFilterBar';
import MediaCard from './media/MediaCard';
import MediaPager from './media/MediaPager';
import MediaDetailDrawer from './media/MediaDetailDrawer';
import { useIsMobile } from '../hooks/useIsMobile';

/** 一頁張數：桌機 48（e2e 依此驗證）；手機兩欄，48 張太長，減半。 */
const PAGE_SIZE = 48;
const PAGE_SIZE_MOBILE = 24;

function toParams(f: MediaFilters, page: number, pageSize: number): Record<string, string> {
  const p: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
  if (f.q) p.q = f.q;
  if (f.tagId) p.tagId = f.tagId;
  if (f.categoryId) p.categoryId = f.categoryId;
  if (f.dateFrom) p.dateFrom = String(f.dateFrom);
  if (f.dateTo) p.dateTo = String(f.dateTo);
  return p;
}

/** 媒體庫：六萬多張照片，靠「刊出時間 + 標籤 + 關鍵字」找，不是靠捲動。 */
export default function MediaPage() {
  const qc = useQueryClient();
  const [f, setF] = useState<MediaFilters>({});
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<MediaRow | null>(null);
  const mobile = useIsMobile();
  const pageSize = mobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const params = toParams(f, page, pageSize);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['media', params],
    queryFn: () => api.mediaList(params),
    placeholderData: (prev) => prev,
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadImage(file),
    onSuccess: () => {
      message.success('已上傳');
      qc.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patch = (p: Partial<MediaFilters>) => {
    setF((s) => ({ ...s, ...p }));
    setPage(1);
  };
  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success('已複製網址');
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="媒體庫"
        subtitle={total ? `共 ${total.toLocaleString()} 張${mobile ? '' : '，可用刊出時間、標籤或關鍵字搜尋'}` : undefined}
        extra={
          <Upload
            showUploadList={false}
            accept="image/*"
            beforeUpload={(file) => {
              upload.mutate(file);
              return false;
            }}
          >
            <Button type="primary" size="large" icon={<UploadOutlined />} loading={upload.isPending}>
              上傳圖片
            </Button>
          </Upload>
        }
      />

      <MediaFilterBar
        categories={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        onChange={patch}
      />

      {isLoading ? (
        <Spin className="block py-24 text-center" />
      ) : items.length === 0 ? (
        <Empty description="找不到符合條件的照片" />
      ) : (
        <div className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 md:grid-cols-4 lg:grid-cols-6">
            {items.map((m) => (
              <MediaCard key={m.id} item={m} onCopy={copy} onOpen={setDetail} />
            ))}
          </div>
          <MediaPager
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(p) => {
              setPage(p);
              // 捲動容器是 App 的 Content（#app-content），window.scrollTo 對它無效
              document.getElementById('app-content')?.scrollTo({ top: 0 });
            }}
          />
        </div>
      )}

      <MediaDetailDrawer item={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
